import type { ValidationErrors } from '@/features/enrollment'

export const validateStep4 = (data: any): ValidationErrors => {
  const errors: ValidationErrors = {}
  
  if (data.isCurrentlyInSchool === undefined || data.isCurrentlyInSchool === null) errors.isCurrentlyInSchool = 'School status is required'
  if (data.isCurrentlyInSchool === true && !data.schoolName) errors.schoolName = 'School name is required'
  if (data.isCurrentlyInSchool === true && !data.schoolAddress) errors.schoolAddress = 'School address is required'
  if (data.isCurrentlyInSchool === true && !data.schoolCity) errors.schoolCity = 'School city is required'
  if (data.isCurrentlyInSchool === true && !data.schoolState) errors.schoolState = 'School state is required'
  if (data.isCurrentlyInSchool === true && !data.schoolZipCode) errors.schoolZipCode = 'School ZIP code is required'
  if (!data.highestEducationLevel) errors.highestEducationLevel = 'Highest education level is required'
  if (!data.firstGamingDate) errors.firstGamingDate = 'First gaming date is required'
  if (!data.averageHoursPerDay) {
    errors.averageHoursPerDay = 'Average hours per day is required'
  } else {
    const hours = parseFloat(data.averageHoursPerDay)
    if (isNaN(hours) || hours < 0.1) {
      errors.averageHoursPerDay = 'Please enter a valid number of hours'
    } else if (hours < 3) {
      errors.averageHoursPerDay = 'Must be at least 3 hours per day'
    } else if (hours > 24) {
      errors.averageHoursPerDay = 'Cannot exceed 24 hours per day'
    }
  }
  // Gaming platforms and video games are auto-populated from Step2, so no validation needed
  if (!data.firstVideoGame) errors.firstVideoGame = 'First video game is required'
  if (!data.monthlySubscriptions || data.monthlySubscriptions.length === 0) errors.monthlySubscriptions = 'At least one subscription option is required'
  if (data.cloudGamingSubscription === undefined || data.cloudGamingSubscription === null) errors.cloudGamingSubscription = 'Cloud gaming subscription status is required'
  if (data.vrAccessories === undefined || data.vrAccessories === null) errors.vrAccessories = 'VR accessories status is required'
  if (data.hasReceipts === undefined || data.hasReceipts === null) errors.hasReceipts = 'Receipts status is required'
  if (!data.monthlySpending) errors.monthlySpending = 'Monthly spending is required'
  if (!data.proofOfGaming || data.proofOfGaming.length === 0) errors.proofOfGaming = 'At least one proof option is required'
  if (data.triedToStopGaming === undefined || data.triedToStopGaming === null) errors.triedToStopGaming = 'Tried to stop gaming status is required'
  if (data.paidForInGameItems === undefined || data.paidForInGameItems === null) errors.paidForInGameItems = 'Paid for in-game items status is required'
  if (data.soldInGameContent === undefined || data.soldInGameContent === null) errors.soldInGameContent = 'Sold in-game content status is required'
  if (data.receivedEnergyDrinkReward === undefined || data.receivedEnergyDrinkReward === null) errors.receivedEnergyDrinkReward = 'Energy drink reward status is required'
  if (data.watchedGamingInfluencers === undefined || data.watchedGamingInfluencers === null) errors.watchedGamingInfluencers = 'Gaming influencers status is required'
  
  return errors
}

export const validateHoursPerDay = (value: string, errors: ValidationErrors, setErrors: (errors: ValidationErrors) => void) => {
  const newErrors = { ...errors }
  
  if (!value) {
    newErrors.averageHoursPerDay = 'Average hours per day is required'
  } else {
    const hours = parseFloat(value)
    if (isNaN(hours) || hours < 0.1) {
      newErrors.averageHoursPerDay = 'Please enter a valid number of hours'
    } else if (hours < 3) {
      newErrors.averageHoursPerDay = 'Must be at least 3 hours per day'
    } else if (hours > 24) {
      newErrors.averageHoursPerDay = 'Cannot exceed 24 hours per day'
    } else {
      delete newErrors.averageHoursPerDay
    }
  }
  
  setErrors(newErrors)
}

export const validateMonthlySpending = (value: string, errors: ValidationErrors, setErrors: (errors: ValidationErrors) => void) => {
  const newErrors = { ...errors }
  
  if (!value) {
    newErrors.monthlySpending = 'Monthly spending amount is required'
  } else {
    const amount = parseFloat(value)
    if (isNaN(amount) || amount < 0) {
      newErrors.monthlySpending = 'Please enter a valid amount'
    } else if (amount < 50) {
      newErrors.monthlySpending = 'Amount must be at least $50'
    } else if (amount > 200) {
      newErrors.monthlySpending = 'Amount cannot exceed $200'
    } else {
      delete newErrors.monthlySpending
    }
  }
  
  setErrors(newErrors)
}

export const getSubscriptionsForGames = (games: string[]) => {
  const gameSubscriptionMap: { [key: string]: string[] } = {
    // Epic Games / Fortnite
    'Fortnite': ['Fortnite Battlepass', 'Fortnite Crew Subscription'],
    
    // PlayStation exclusives/related
    'Essential Gaming': ['Playstation Essential', 'Playstation Extra', 'Playstation Premium'],
    'Roblox': ['Roblox Premim'],
    
    // Xbox Game Pass games
    'Call of Duty': ['Game Pass Ultimate', 'Game Pass Core', 'XBox Live Gold', 'EA Play', 'EA Play Pro'],
    'Battlefield': ['Game Pass Ultimate', 'EA Play', 'EA Play Pro'],
    'Need for Speed': ['Game Pass Ultimate', 'EA Play', 'EA Play Pro'],
    'Dead by Daylight': ['Game Pass Ultimate', 'Game Pass Core'],
    
    // Multi-platform popular games
    'Grand Theft Auto': ['Game Pass Ultimate', 'Playstation Premium', 'NVIDIA GeForce NOW'],
    'Rainbow Six': ['Game Pass Ultimate', 'Ubisoft Classics', 'Ubisoft Premium'],
    'Saints Row': ['Game Pass Ultimate'],
    'Rocket League': ['Game Pass Ultimate'],
    'Overwatch': ['Game Pass Ultimate'],
    'PUBG: Battlegrounds': ['Game Pass Ultimate'],
    'NBA 2K': ['Game Pass Ultimate'],
    'Minecraft': ['Game Pass Ultimate', 'Apple Arcade'],
    'Diablo': ['Game Pass Ultimate'],
    'Apex Legends': ['Game Pass Ultimate', 'EA Play', 'EA Play Pro'],
    
    // Custom/Indie games
    'My Hero': ['Game Pass Ultimate'],
    'Mine Quest': ['Apple Arcade', 'Google Play Pass'],
  }

  const suggestedSubscriptions = new Set<string>()
  
  games.forEach(game => {
    const subscriptions = gameSubscriptionMap[game]
    if (subscriptions) {
      subscriptions.forEach(sub => suggestedSubscriptions.add(sub))
    }
  })

  return Array.from(suggestedSubscriptions)
}